import type {
  Appointment,
  CarePlan,
  Encounter,
  ServiceRequest,
} from '@medplum/fhirtypes';

import { activityStickRef, isSpineStick } from './instance';
import {
  type FrequencySegment,
  type VisitOverride,
  formatFrequencyHeadline,
  segmentIntervalDays,
  segmentVisitCount,
  sessionFrequencyOf,
  totalVisitCount,
  visitOverridesOf,
} from './session-frequency';

export type LadderVisitStatus = 'completed' | 'current' | 'upcoming';

export type LadderVisit = {
  number: number;
  date: string | undefined;
  status: LadderVisitStatus;
  booked: boolean;
  pinned: boolean;
  gate: boolean;
  encounterId?: string;
  appointmentId?: string;
};

export type LadderNextVisit = {
  number: number;
  date: string | undefined;
  booked: boolean;
  appointmentId?: string;
};

export type VisitLadder = {
  visits: LadderVisit[];
  total: number | null;
  openEnded: boolean;
  completedCount: number;
  headline: string;
  nextVisit: LadderNextVisit | null;
};

export type LinkedEncounter = {
  id: string;
  date: string | undefined;
  status: Encounter['status'];
  appointmentId?: string;
};

export type LinkedAppointment = {
  id: string;
  date: string | undefined;
  status: Appointment['status'];
};

export type ProjectLadderArgs = {
  segments: FrequencySegment[];
  startDate: string;
  overrides?: VisitOverride[];
  phaseVisitTargets?: (number | undefined)[];
  encounters?: LinkedEncounter[];
  appointments?: LinkedAppointment[];
};

const DAY_MS = 86_400_000;

function parseDateMs(isoDate: string): number {
  return Date.parse(`${isoDate.slice(0, 10)}T00:00:00Z`);
}

function isoDate(ms: number): string {
  return new Date(Math.round(ms)).toISOString().slice(0, 10);
}

function segmentForVisit(
  segments: FrequencySegment[],
  visitNumber: number,
): FrequencySegment {
  let consumed = 0;
  for (const segment of segments) {
    const count = segmentVisitCount(segment);
    if (count === null) return segment;
    consumed += count;
    if (visitNumber <= consumed) return segment;
  }
  return segments[segments.length - 1]!;
}

function gateNumbers(
  phaseVisitTargets: (number | undefined)[] | undefined,
  total: number | null,
): Set<number> {
  const gates = new Set<number>();
  let sum = 0;
  for (const target of phaseVisitTargets ?? []) {
    if (target === undefined) break;
    sum += target;
    if (total === null || sum < total) gates.add(sum);
  }
  return gates;
}

function isLiveEncounter(encounter: LinkedEncounter): boolean {
  return (
    encounter.status !== 'cancelled' && encounter.status !== 'entered-in-error'
  );
}

const BOOKED_APPOINTMENT_STATUSES = new Set<Appointment['status']>([
  'booked',
  'arrived',
  'checked-in',
]);

function byDate<T extends { date: string | undefined }>(a: T, b: T): number {
  return (a.date ?? '9999').localeCompare(b.date ?? '9999');
}

export function projectVisitLadder(args: ProjectLadderArgs): VisitLadder {
  const { segments, startDate } = args;
  const total = totalVisitCount(segments);
  const openEnded = total === null;
  const boundedCount = segments.reduce(
    (sum, s) => sum + (segmentVisitCount(s) ?? 0),
    0,
  );

  const encounters = (args.encounters ?? [])
    .filter(isLiveEncounter)
    .sort(byDate);
  const consumedAppointments = new Set(
    encounters
      .map((e) => e.appointmentId)
      .filter((id): id is string => id !== undefined),
  );
  const appointments = (args.appointments ?? [])
    .filter(
      (a) =>
        BOOKED_APPOINTMENT_STATUSES.has(a.status) &&
        !consumedAppointments.has(a.id),
    )
    .sort(byDate);
  const pinByNumber = new Map(
    (args.overrides ?? []).map((o) => [o.visit, o.date]),
  );

  const dotCount = Math.max(
    boundedCount,
    encounters.length + appointments.length,
  );
  const gates = gateNumbers(args.phaseVisitTargets, total);

  const visits: LadderVisit[] = [];
  let cursorMs = parseDateMs(startDate);
  let nextVisit: LadderNextVisit | null = null;

  for (let number = 1; number <= dotCount; number++) {
    const encounter = encounters[number - 1];
    const appointment = encounter
      ? undefined
      : appointments[number - 1 - encounters.length];
    const pinned = !encounter && !appointment && pinByNumber.has(number);

    let date: string | undefined;
    if (encounter) {
      date = encounter.date;
    } else if (appointment) {
      date = appointment.date;
    } else if (pinned) {
      date = pinByNumber.get(number);
    }

    if (date !== undefined) {
      cursorMs = parseDateMs(date);
    } else {
      if (number > 1)
        cursorMs +=
          segmentIntervalDays(segmentForVisit(segments, number)) * DAY_MS;
      date = isoDate(cursorMs);
    }

    const status: LadderVisitStatus = encounter
      ? encounter.status === 'finished'
        ? 'completed'
        : 'current'
      : 'upcoming';

    if (!encounter && nextVisit === null) {
      nextVisit = {
        number,
        date,
        booked: appointment !== undefined,
        ...(appointment ? { appointmentId: appointment.id } : {}),
      };
    }

    visits.push({
      number,
      date,
      status,
      booked: appointment !== undefined,
      pinned,
      gate: gates.has(number),
      ...(encounter ? { encounterId: encounter.id } : {}),
      ...(encounter?.appointmentId
        ? { appointmentId: encounter.appointmentId }
        : appointment
          ? { appointmentId: appointment.id }
          : {}),
    });
  }

  if (nextVisit === null && openEnded) {
    const number = dotCount + 1;
    nextVisit = {
      number,
      date: isoDate(
        cursorMs +
          (dotCount === 0
            ? 0
            : segmentIntervalDays(segmentForVisit(segments, number)) * DAY_MS),
      ),
      booked: false,
    };
  }

  return {
    visits,
    total,
    openEnded,
    completedCount: encounters.filter(
      (encounter) => encounter.status === 'finished',
    ).length,
    headline: formatFrequencyHeadline(segments),
    nextVisit,
  };
}

export type LadderResources = {
  carePlan: CarePlan;
  serviceRequests: ServiceRequest[];
  encounters: Encounter[];
  appointments: Appointment[];
};

export function ladderArgsFromResources(
  resources: LadderResources,
): ProjectLadderArgs | null {
  const { carePlan } = resources;
  const segments = sessionFrequencyOf(carePlan);
  const startDate = carePlan.period?.start?.slice(0, 10);
  if (!segments || !startDate) return null;

  const sticksById = new Map(
    resources.serviceRequests
      .filter((sr) => isSpineStick(sr) && sr.id)
      .map((sr) => [sr.id!, sr]),
  );
  const orderedSticks: ServiceRequest[] = [];
  for (const activity of carePlan.activity ?? []) {
    const id = activityStickRef(activity)?.split('/')[1];
    const stick = id ? sticksById.get(id) : undefined;
    if (stick) {
      orderedSticks.push(stick);
      sticksById.delete(id!);
    }
  }
  orderedSticks.push(...sticksById.values());

  const appointments: LinkedAppointment[] = resources.appointments
    .filter((a): a is Appointment & { id: string } => Boolean(a.id))
    .map((a) => ({ id: a.id, date: a.start?.slice(0, 10), status: a.status }));
  const appointmentDateById = new Map(appointments.map((a) => [a.id, a.date]));

  const encounters: LinkedEncounter[] = resources.encounters
    .filter((e): e is Encounter & { id: string } => Boolean(e.id))
    .map((e) => {
      const appointmentId = e.appointment?.[0]?.reference?.split('/')[1];
      return {
        id: e.id,
        date:
          e.period?.start?.slice(0, 10) ??
          (appointmentId ? appointmentDateById.get(appointmentId) : undefined),
        status: e.status,
        ...(appointmentId ? { appointmentId } : {}),
      };
    });

  return {
    segments,
    startDate,
    overrides: visitOverridesOf(carePlan),
    phaseVisitTargets: orderedSticks.map(
      (stick) => stick.quantityQuantity?.value,
    ),
    encounters,
    appointments,
  };
}
